/**
 * 智能服務轉導頁的純決策函式（無副作用，可單元測）。
 * 元件外殼只負責計時器、事件監聽與畫面呈現，「要進哪個階段、要等多久」一律集中在此。
 *
 * 刻意不 import 任何別名模組（@Util 等）：本站 jest 設定未掛 moduleNameMapper，
 * 純函式模組維持零相依才能直接被測。
 */

/**
 * 頁面階段
 * @readonly
 */
export const PHASE = Object.freeze({
    LOADING    : 'loading',     // 讀取智能服務資訊中
    REDIRECTING: 'redirecting', // 畫面已完整顯示，提示後送出轉導
    FALLBACK   : 'fallback',    // 自動轉導未生效，改由使用者手動點擊
    UNBOUND    : 'unbound',     // 未開通智能服務
});

/** 送出轉導前，至少讓畫面完整顯示的時間（ms） */
export const MIN_DISPLAY_MS = 1500;

/**
 * 後端回報 delayable 時改用的等待時間（ms）。
 * 代表宇康端剛收到開通任務、資料尚未建好，必須留時間給對方建檔，不可縮短。
 */
export const UCARE_PROVISION_WAIT_MS = 6000;

/** 「即將前往」提示停留的時間（ms），確保使用者看得到轉導提示才真的跳走 */
export const REDIRECT_NOTICE_MS = 800;

/** 送出轉導後，判定「沒有跳成功」的觀察時間（ms） */
export const REDIRECT_TIMEOUT_MS = 3000;

/**
 * 判斷後端給的轉導網址是否可用。
 * @param {*} pageUrl
 * @returns {Boolean}
 */
const hasRedirectUrl = (pageUrl) => typeof pageUrl === 'string' && pageUrl.trim() !== '';

/**
 * 依後端回應決定下一個階段、轉導網址與等待時間。
 * API 失敗（resp 為 null / undefined）與「未開通」一律視為 UNBOUND。
 * @param {{pageUrl?: String, delayable?: Boolean}|null|undefined} resp 後端 device-order-info 回應
 * @returns {{phase: String, pageUrl: String, waitMs: Number}}
 */
export const resolveRedirectPlan = (resp) => {
    const { pageUrl, delayable } = resp || {};

    if (!hasRedirectUrl(pageUrl)) {
        return { phase: PHASE.UNBOUND, pageUrl: '', waitMs: 0 };
    }

    return {
        phase  : PHASE.REDIRECTING,
        pageUrl,
        waitMs : delayable ? UCARE_PROVISION_WAIT_MS : MIN_DISPLAY_MS,
    };
};

/**
 * bfcache 還原（多半是使用者從宇康頁按返回）時該進入的階段。
 * 此時不可再自動轉導，否則會陷入「按返回 → 又被送走」的迴圈，改切到手動模式讓使用者自行決定。
 *
 * 只有「已經送出過自動轉導」才切手動模式：若轉導都還沒送出（例如讀取中被系統通知打斷、
 * App 切背景再回前景），使用者根本還沒被帶去任何地方，此時切手動模式反而會拿到尚未取得的
 * 空網址，做出一顆按了只會原地重整的死按鈕。
 *
 * @param {String} prevPhase 還原前的階段
 * @param {Boolean} persisted PageTransitionEvent.persisted
 * @param {Boolean} hasRedirected 是否已經送出過自動轉導
 * @returns {String} 還原後應套用的階段
 */
export const resolvePhaseOnPageShow = (prevPhase, persisted, hasRedirected) => {
    // 非 bfcache 還原（一般首次載入）不介入
    if (!persisted) return prevPhase;
    // 未開通本來就沒有轉導行為，維持原畫面
    if (prevPhase === PHASE.UNBOUND) return prevPhase;
    // 還沒送出過轉導，維持原流程讓它自然走完
    if (!hasRedirected) return prevPhase;

    return PHASE.FALLBACK;
};

/**
 * 「畫面已完整顯示」的計時到點時該進入的階段。
 *
 * 計時期間使用者可能已被切到 FALLBACK（bfcache 還原）或畫面已變成 UNBOUND，
 * 此時不可把階段覆蓋回自動轉導，否則會重演這支模組要防止的「又被自動送走」。
 * 僅在仍停留於 LOADING 時才推進。
 *
 * @param {String} prevPhase 計時到點當下的階段
 * @param {String} plannedPhase resolveRedirectPlan 當初規劃要進入的階段
 * @returns {String} 應套用的階段
 */
export const resolvePhaseOnDisplayTimeout = (prevPhase, plannedPhase) =>
    (prevPhase === PHASE.LOADING ? plannedPhase : prevPhase);
